import React, { useEffect } from "react";
import { X, Play, Clock, Tag, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

const VideoModal = ({ tape, onClose }) => {
  useEffect(() => {
    if (!tape) return;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [tape, onClose]);

  if (!tape) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/85 backdrop-blur-md"
        onClick={onClose}
      />

      <div
        className="relative z-10 w-full max-w-5xl rounded-2xl overflow-hidden border border-[#2a221b]"
        style={{
          background: "linear-gradient(180deg, #14100c 0%, #0e0b09 100%)",
          boxShadow: "0 40px 120px rgba(0,0,0,0.7)",
        }}
      >
        {/* Top bar */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#2a221b]">
          <div className="flex items-center gap-3">
            <span
              className="w-2.5 h-2.5 rounded-full rec-dot"
              style={{ background: tape.accent }}
            />
            <span className="text-[11px] mono uppercase tracking-widest text-[#f2ece1]/80">
              Now Playing • CH.03
            </span>
            <span className="hidden md:inline text-[11px] mono text-[#f2ece1]/40">
              TC 00:00:00:00
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-[#1a1410] text-[#f2ece1]/70 hover:text-[#f2ece1] transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Video */}
        <div className="relative aspect-video bg-black scanlines">
          <iframe
            key={tape.id}
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube-nocookie.com/embed/${tape.youtubeId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
            title={tape.title}
            frameBorder="0"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
          />
        </div>

        {/* Meta */}
        <div className="p-5 md:p-6 grid md:grid-cols-3 gap-5 md:gap-8">
          <div className="md:col-span-2">
            <div
              className="text-[11px] mono uppercase tracking-widest mb-2"
              style={{ color: tape.accent }}
            >
              {tape.subtitle} • {tape.year}
            </div>
            <h3
              className="text-3xl md:text-4xl font-normal leading-tight"
              style={{ fontFamily: "'Instrument Serif', serif" }}
            >
              {tape.title}
            </h3>
            <p className="mt-3 text-sm text-[#f2ece1]/70 leading-relaxed max-w-xl">
              {tape.description}
            </p>
          </div>
          <div className="space-y-3 md:border-l md:border-[#2a221b] md:pl-6">
            <div className="flex items-center gap-2 text-[12px] text-[#f2ece1]/70">
              <Tag className="w-3.5 h-3.5" style={{ color: tape.accent }} />
              <span className="mono uppercase tracking-wider">{tape.category}</span>
            </div>
            <div className="flex items-center gap-2 text-[12px] text-[#f2ece1]/70">
              <Clock className="w-3.5 h-3.5" style={{ color: tape.accent }} />
              <span className="mono">Runtime {tape.runtime}</span>
            </div>
            <div className="flex items-center gap-2 text-[12px] text-[#f2ece1]/70">
              <Play className="w-3.5 h-3.5" style={{ color: tape.accent }} />
              <span className="mono uppercase tracking-wider">
                CAT #{tape.id.toUpperCase()}-{tape.year}
              </span>
            </div>
            <a
              href={`https://www.youtube.com/watch?v=${tape.youtubeId}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 pt-2 text-[12px] mono uppercase tracking-wider text-[#f2ece1]/70 hover:text-[#f5b14e] transition-colors"
            >
              Open on YouTube &#x2197;
            </a>
            <Link
              to={`/tape/${tape.id}`}
              onClick={onClose}
              className="inline-flex items-center gap-2 pt-1 text-[12px] mono uppercase tracking-wider text-[#f2ece1]/70 hover:text-[#f5b14e] transition-colors"
            >
              View full details <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoModal;
